import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminVerdaCreateVolumeProducts } from '../../../hooks/useAdminVerda';
import type { VerdaVolumeProductCreateResponse, VerdaVolumeProductCreateResult } from '../../../types/models/Verda';
import { formatCurrencyAmount, formatUnitPrice } from '../../../utils/money';

interface VerdaCreateStorageProductsModalProps {
  onClose: () => void;
  /** Called after a successful run (whatever the per-type outcome), so the tab can refresh its table. */
  onCreated: (response: VerdaVolumeProductCreateResponse) => void;
}

const STATUS_LABEL: Record<
  VerdaVolumeProductCreateResult['status'],
  { text: string; color: 'green' | 'grey' | 'red' }
> = {
  created: { text: 'Created', color: 'green' },
  exists: { text: 'Already existed', color: 'grey' },
  failed: { text: 'Failed', color: 'red' },
};

/**
 * Confirm, then show the outcome of, `POST /admin/verda/volume-products` (docs/api-types/admin-verda.md
 * "Storage products"). No body: the backend creates whatever is missing among the three mapped Verda
 * volume types, prices it from Verda's live EUR price × 1.20 (rounded up to 4 decimals), and leaves a
 * linked type untouched (`exists`) — so the run is safe to repeat. A per-type failure is a `failed` row
 * with its reason, inside a success. Mounted only while open, so each run starts on the confirmation.
 */
function VerdaCreateStorageProductsModal({ onClose, onCreated }: VerdaCreateStorageProductsModalProps) {
  const { create, loading } = useAdminVerdaCreateVolumeProducts();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<VerdaVolumeProductCreateResponse | null>(null);

  const handleConfirm = async () => {
    setError(null);
    const outcome = await create();
    if (outcome.status === 'success') {
      setResponse(outcome.response);
      onCreated(outcome.response);
      const { created, exists, failed } = outcome.response.summary;
      addNotification({
        title: failed > 0 ? 'Storage products created with failures' : 'Storage products created',
        srTitle: failed > 0 ? 'Warning alert' : 'Success alert',
        description: `${created} created, ${exists} already existed, ${failed} failed.`,
        variant: failed > 0 ? 'warning' : 'success',
      });
      setDrawerOpen(true);
      return;
    }
    setError(outcome.message);
    addNotification({
      title: 'Could not create the storage products',
      srTitle: 'Error alert',
      description: outcome.message,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const renderConfirm = () => (
    <>
      {error !== null && (
        <Alert variant="danger" isInline title="Could not create the storage products" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      <Content component="p">
        Creates one monthly per-GB product per Verda volume type (NVMe, HDD, shared NVMe filesystem) in the “GPU
        Storage” category at the Verda EUR price × 1.20, rounded up to 4 decimals. They are not listed on the customer
        pricing page, but every customer can pick them in a GPU budget. The NVMe cost also prices the fixed 100 GB
        server disk in customer budgets.
      </Content>
      <Content component="ul">
        <Content component="li">Volume types that already have a product are left unchanged (never re-priced).</Content>
        <Content component="li">
          Verda&apos;s price is read once per missing type; a type whose read fails is reported and the others are still
          created.
        </Content>
      </Content>
    </>
  );

  const renderResult = (result: VerdaVolumeProductCreateResponse) => (
    <>
      <Flex spaceItems={{ default: 'spaceItemsSm' }} className="pf-v6-u-mb-md">
        <FlexItem>
          <Label color="green">{result.summary.created} created</Label>
        </FlexItem>
        <FlexItem>
          <Label color="grey">{result.summary.exists} already existed</Label>
        </FlexItem>
        <FlexItem>
          <Label color={result.summary.failed > 0 ? 'red' : 'grey'}>{result.summary.failed} failed</Label>
        </FlexItem>
      </Flex>
      {result.results.length > 0 && (
        <Table aria-label="Storage product creation results" variant="compact">
          <Thead>
            <Tr>
              <Th>Volume type</Th>
              <Th>Result</Th>
              <Th>Product</Th>
              <Th modifier="nowrap">Verda cost / GB / month</Th>
              <Th modifier="nowrap">Sale price / GB / month</Th>
            </Tr>
          </Thead>
          <Tbody>
            {result.results.map((row) => (
              <Tr key={row.volume_type}>
                <Td dataLabel="Volume type">
                  <span className="pf-v6-u-font-family-monospace">{row.volume_type}</span>
                </Td>
                <Td dataLabel="Result">
                  <Label isCompact color={STATUS_LABEL[row.status].color}>
                    {STATUS_LABEL[row.status].text}
                  </Label>
                  {row.status === 'failed' && (
                    <div>
                      <Content component="small">{row.reason}</Content>
                    </div>
                  )}
                </Td>
                <Td dataLabel="Product">
                  {row.status === 'failed' ? (
                    '—'
                  ) : (
                    <span className="pf-v6-u-font-family-monospace">{row.product.product_key}</span>
                  )}
                </Td>
                <Td dataLabel="Verda cost / GB / month" modifier="nowrap">
                  {row.status === 'created' ? formatCurrencyAmount(row.cost_per_gb_month, result.currency) : '—'}
                </Td>
                <Td dataLabel="Sale price / GB / month" modifier="nowrap">
                  {row.status === 'created'
                    ? formatCurrencyAmount(row.sale_price_per_gb_month, result.currency)
                    : row.status === 'exists' && row.product.default_unit_price !== null
                      ? formatUnitPrice(row.product.default_unit_price)
                      : '—'}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </>
  );

  return (
    <Modal
      variant={response ? 'medium' : 'small'}
      isOpen
      onClose={loading ? undefined : onClose}
      aria-labelledby="verda-create-storage-products-title"
    >
      <ModalHeader
        title={response ? 'Storage product creation results' : 'Create missing storage products'}
        labelId="verda-create-storage-products-title"
        description={response ? undefined : 'From the Verda volume types'}
      />
      <ModalBody>{response ? renderResult(response) : renderConfirm()}</ModalBody>
      <ModalFooter>
        {response ? (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="primary" onClick={() => void handleConfirm()} isLoading={loading} isDisabled={loading}>
              Create missing storage products
            </Button>
            <Button variant="link" onClick={onClose} isDisabled={loading}>
              Cancel
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default VerdaCreateStorageProductsModal;
